/**
 * Download filename helpers for generated assets.
 *
 * Shared by `GenerationSlot` and `MediaPreviewModal` so a downloaded file always
 * carries the right media label and extension: `generated-3d-42.glb`, not
 * `generated-image-42.jpg`.
 */

import {
  AUDIO_EXTENSIONS,
  VIDEO_EXTENSIONS,
  IMAGE_EXTENSIONS,
  MODEL_EXTENSIONS,
  is3DContentType,
  resolveIs3D,
  getMediaTypeFromUrl
} from './mediaTypeDetection';

export type DownloadMediaType = 'image' | 'video' | 'audio' | 'text' | '3d';

const DEFAULT_EXT: Record<DownloadMediaType, string> = {
  image: 'jpg',
  video: 'mp4',
  audio: 'mp3',
  text: 'txt',
  '3d': 'glb'
};

// Extension sets come from `mediaTypeDetection` so there is exactly one list per
// media type in the app. `svg` is added here only for the download path: it is a
// legitimate extension on existing assets even though upload paths reject it.
const ALLOWED_EXTS: Record<DownloadMediaType, string[]> = {
  image: [...IMAGE_EXTENSIONS, 'svg'],
  video: VIDEO_EXTENSIONS,
  audio: AUDIO_EXTENSIONS,
  text: ['txt'],
  '3d': MODEL_EXTENSIONS
};

/**
 * Extension → MIME for the download path. Lives here, next to the extension
 * *selection* logic, so the filename and the `Content-Type` a download endpoint
 * sends are two reads of ONE table and cannot contradict each other.
 *
 * `svg` is deliberately absent: download endpoints send
 * `Content-Disposition: attachment`, and letting a hostile SVG carry
 * `image/svg+xml` would re-label it a renderable document. Unmapped extensions
 * fall through to `application/octet-stream` at the call site.
 */
export const EXTENSION_MIME: Record<string, string> = {
  png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', webp: 'image/webp',
  gif: 'image/gif', bmp: 'image/bmp', tiff: 'image/tiff', heic: 'image/heic', heif: 'image/heif',
  mp4: 'video/mp4', webm: 'video/webm', mov: 'video/quicktime',
  avi: 'video/x-msvideo', mkv: 'video/x-matroska', m4v: 'video/x-m4v',
  mp3: 'audio/mpeg', mpeg: 'audio/mpeg', mpga: 'audio/mpeg', wav: 'audio/wav',
  ogg: 'audio/ogg', oga: 'audio/ogg', flac: 'audio/flac', m4a: 'audio/mp4',
  aac: 'audio/aac', weba: 'audio/webm',
  glb: 'model/gltf-binary', gltf: 'model/gltf+json', obj: 'model/obj',
  fbx: 'model/fbx', stl: 'model/stl', usdz: 'model/vnd.usdz+zip',
  txt: 'text/plain'
};

// MIME → extension. Several extensions share one MIME, so **the key order of
// EXTENSION_MIME above is load-bearing**: first entry wins, which picks the
// canonical `jpg` / `mp3` / `ogg` over `jpeg` / `mpeg`+`mpga` / `oga`.
// `tests/unit/download-naming.test.ts` pins those three choices.
const MIME_EXTENSION: Record<string, string> = Object.entries(EXTENSION_MIME).reduce(
  (acc, [ext, mime]) => {
    if (!(mime in acc)) acc[mime] = ext;
    return acc;
  },
  {} as Record<string, string>
);

/**
 * Strip MIME parameters and normalize case: `Video/MP4; codecs=avc1` → `video/mp4`.
 *
 * Values reaching these helpers are RAW `content-type` response headers (the
 * download endpoint reads the upstream header directly, and a freshly
 * dispatched slot carries whatever `processContentUrl` saw), and MIME types are
 * case-insensitive per RFC 9110. Mirrors the same normalization in
 * `r2.ts:getExtensionFromContentType`, which consumes the very same headers.
 */
function normalizeContentType(contentType?: string | null): string | null {
  if (!contentType) return null;
  return contentType.split(';')[0].trim().toLowerCase() || null;
}

/**
 * Is this a usable per-asset media `content-type`, i.e. one of the four media
 * families rather than `application/octet-stream` / `text/plain`?
 *
 * Lives here rather than at the call site so the *admission* check and the
 * *resolution* below share one `normalizeContentType`. A local regex on the raw
 * header drifts case-sensitive: R2/CDN origins may echo `VIDEO/WEBM` or
 * `MODEL/GLTF+JSON; charset=utf-8`, which is valid per RFC 9110 but would be
 * rejected here, collapsing the precise subtype back to the broad stored
 * `content_type` and — for an extensionless provider URL — losing `.webm`/`.gltf`
 * to the family default.
 */
export function isMediaContentType(contentType?: string | null): boolean {
  const bare = normalizeContentType(contentType);
  return bare ? /^(image|video|audio|model)\//.test(bare) : false;
}

/**
 * Map a generation `content_type` to the label used in the download filename.
 *
 * The URL is consulted as a fallback so a model whose `content_type` is missing
 * or stale still downloads as a model — callers must NOT pre-substitute `'3d'`
 * themselves. Unknown/absent content types stay `'image'` — the historic default.
 */
export function resolveDownloadMediaType(
  rawContentType?: string | null,
  url?: string | null
): DownloadMediaType {
  const contentType = normalizeContentType(rawContentType);
  // Mirrors the render branches (GenerationSlot / MediaPreviewModal): 3D is
  // decided per asset, so a texture PNG inside a 3D generation is not named
  // `generated-3d-42.glb` — and a `.glb` whose content_type is stale still is.
  if (resolveIs3D(contentType, url)) return '3d';
  if (contentType === 'text') return 'text';
  if (contentType?.startsWith('video')) return 'video';
  if (contentType?.startsWith('audio')) return 'audio';
  // Companion asset inside a 3D generation: the generation type says '3d' but
  // this asset was ruled out above, so the URL is the only signal left.
  if (is3DContentType(contentType)) return getMediaTypeFromUrl(url || '');
  return 'image';
}

/** Lowercase extension from a URL, ignoring query string and hash. */
export function extensionFromUrlPath(url?: string | null): string | null {
  if (!url) return null;
  const clean = url.split('?')[0].split('#')[0];
  const lastSegment = clean.split('/').pop() || '';
  if (!lastSegment.includes('.')) return null;
  const ext = lastSegment.split('.').pop()?.toLowerCase();
  return ext || null;
}

/**
 * Extension for a recognised MIME, ignoring parameters and case
 * (`model/gltf+json; charset=utf-8` → `gltf`). `null` for anything the
 * download table doesn't map — including `image/svg+xml` by design.
 */
export function extensionFromContentType(contentType?: string | null): string | null {
  const bare = normalizeContentType(contentType);
  return bare ? MIME_EXTENSION[bare] || null : null;
}

/**
 * Pick the extension for a download, in descending order of confidence:
 * the URL's own extension, then the precise MIME's subtype, then the media
 * family's default.
 *
 * The MIME step is not optional. Provider URLs (Dify `file-preview`, presigned
 * CDN paths) frequently carry no extension at all, and collapsing straight to
 * the family default there loses a subtype the caller actually knew: a
 * `model/gltf+json` saved as `.glb` is not a mislabel but a broken file — GLB
 * parsers reject JSON. Same class of error, less visible, for `image/png` →
 * `.jpg`, `video/webm` → `.mp4`, `audio/ogg` → `.mp3`.
 *
 * Both candidates are filtered through `ALLOWED_EXTS` for the resolved media
 * type, so a content type disagreeing with the media type (a stale
 * `image/jpeg` on a `.glb` URL) can never inject a foreign extension.
 */
export function resolveDownloadExtension(contentType: string | null | undefined, url?: string | null): string {
  const mediaType = resolveDownloadMediaType(contentType, url);
  const allowed = ALLOWED_EXTS[mediaType];

  const urlExt = extensionFromUrlPath(url);
  if (urlExt && allowed.includes(urlExt)) return urlExt;

  const mimeExt = extensionFromContentType(contentType);
  if (mimeExt && allowed.includes(mimeExt)) return mimeExt;

  return DEFAULT_EXT[mediaType];
}

/**
 * Full `generated-<type>-<id>[-<index>].<ext>` filename.
 * `index` is 1-based and omitted when not supplied (single-asset surfaces).
 */
export function buildDownloadFilename(
  contentType: string | null | undefined,
  slotId: string | number,
  url?: string | null,
  index?: number
): string {
  const mediaType = resolveDownloadMediaType(contentType, url);
  const extension = resolveDownloadExtension(contentType, url);
  const suffix = typeof index === 'number' ? `-${index}` : '';
  return `generated-${mediaType}-${slotId}${suffix}.${extension}`;
}
